import { DocsCallout, DocsContent, DocsDiagram, DocsLayout, H2, H3 } from '@/components/docs'

export default function DocsPipelinesText() {
  return (
    <DocsLayout>
      <DocsContent
        title="Text Extraction Pipeline"
        description="How XENDREL extracts entities and relationships from unstructured text documents using LLM-powered analysis and the POLE schema."
        previousPage={{ title: 'Pipeline Overview', href: '/docs/pipelines' }}
        nextPage={{ title: 'Tabular Extraction', href: '/docs/pipelines/tabular' }}
      >
        <H2 id="overview">Overview</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          The Text Extraction pipeline processes unstructured documents — PDFs, Word files, and
          plain text — by passing content through an LLM that identifies entities, their properties,
          and relationships according to the active extraction schema (POLE).
        </p>

        <DocsDiagram>{`Document (PDF/DOCX/TXT)
    ↓
Phase 1: Parse → text chunks → Typesense index
    ↓
Phase 2: Text Extraction
    ├─ Load extraction schema (POLE)
    ├─ For each text chunk:
    │   ├─ LLM identifies entities (Person, Company, Location, ...)
    │   ├─ LLM identifies relationships (WORKS_FOR, LOCATED_AT, ...)
    │   └─ Properties extracted (name, address, date, amount, ...)
    ├─ Entity deduplication and merging
    └─ MERGE entities + relationships → Neo4j`}</DocsDiagram>

        <H2 id="supported-formats">Supported Formats</H2>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">PDF</strong> — Standard and scanned (OCR)
              documents
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">DOCX</strong> — Microsoft Word documents
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">TXT</strong> — Plain text files
            </span>
          </li>
        </ul>

        <H2 id="extraction-process">Extraction Process</H2>

        <H3 id="chunking">Chunking Strategy</H3>

        <p className="text-foreground mb-4 leading-relaxed">
          Documents are split into chunks to fit within LLM context windows while preserving enough
          context for accurate extraction. Each chunk overlaps slightly with adjacent chunks to
          avoid missing entities that span chunk boundaries.
        </p>

        <H3 id="entity-extraction">Entity Extraction</H3>

        <p className="text-foreground mb-4 leading-relaxed">For each chunk, the LLM receives:</p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>The text content of the chunk</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              The active extraction schema (entity types, relationship types, valid patterns)
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Instructions to extract structured entities with properties</span>
          </li>
        </ul>

        <p className="text-foreground mb-4 leading-relaxed">
          The LLM returns structured JSON with identified entities, their types, properties, and
          relationships between them. These are validated against the schema before insertion.
        </p>

        <H3 id="entity-merging">Entity Merging</H3>

        <p className="text-foreground mb-4 leading-relaxed">
          When the same entity appears in multiple chunks (e.g., a person mentioned on pages 1 and
          5), XENDREL merges them using the{' '}
          <code className="text-amber-600 dark:text-amber-400 bg-muted px-1 rounded">MERGE</code>{' '}
          operation in Neo4j. Entities are matched by their primary identifier (name + type), and
          properties are accumulated across mentions.
        </p>

        <H2 id="output">Output</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          All extracted data is written to the Neo4j knowledge graph:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Nodes</strong> — One per unique entity, labeled
              with its type (Person, Company, etc.)
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Relationships</strong> — Connections between
              entities (WORKS_FOR, OWNS, LOCATED_AT)
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Properties</strong> — Attributes on nodes and
              relationships (names, dates, amounts)
            </span>
          </li>
        </ul>

        <p className="text-foreground mb-4 leading-relaxed">
          Each node is tagged with{' '}
          <code className="text-amber-600 dark:text-amber-400 bg-muted px-1 rounded">case_id</code>{' '}
          and{' '}
          <code className="text-amber-600 dark:text-amber-400 bg-muted px-1 rounded">
            in_case=true
          </code>{' '}
          for case isolation, and linked to the source document via{' '}
          <code className="text-amber-600 dark:text-amber-400 bg-muted px-1 rounded">
            workflow_run_id
          </code>
          .
        </p>

        <H2 id="schema-configuration">Schema Configuration</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          The extraction schema controls what the LLM looks for. The default POLE schema includes
          entity types commonly needed in investigations:
        </p>

        <DocsDiagram>{`Entity Types:
  Person, Company, Location, Vehicle, Phone, Email,
  BankAccount, Document, Event, Crime, Evidence, ...

Relationship Types:
  WORKS_FOR, OWNS, HAS_PHONE, HAS_EMAIL, LOCATED_AT,
  PARTICIPATED_IN, WITNESSED, RELATED_TO, ...

Valid Patterns:
  (Person) -[WORKS_FOR]-> (Company)
  (Person) -[HAS_PHONE]-> (Phone)
  (Person) -[LOCATED_AT]-> (Location)
  (Company) -[REGISTERED_ADDRESS]-> (Address)
  ...`}</DocsDiagram>

        <DocsCallout variant="tip" title="Custom Schemas">
          You can create custom extraction schemas with additional entity types or relationship
          types specific to your investigation domain. Navigate to the Schema page to manage
          schemas.
        </DocsCallout>

        <H2 id="best-practices">Best Practices</H2>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Document quality matters</strong> — Clear,
              well-formatted text yields better extraction. Low-quality scans may produce OCR
              errors.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Review extractions</strong> — Always verify key
              entities in the graph. LLM extraction is probabilistic, not deterministic.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Use the right pipeline</strong> — If your document
              is primarily tabular data (bank statements, transaction lists), use the Tabular
              pipeline instead for more accurate column-by-column extraction.
            </span>
          </li>
        </ul>
      </DocsContent>
    </DocsLayout>
  )
}
